import type { CreateFormData } from "@/components/create/types";
import type { DesignBrief } from "@/types/createAgent";
import type { DesignSpec } from "@/types/designSpec";
import { deriveDesignBrief } from "./designBrief";
import { compileDesignSpec } from "./designSpec";
import { committedDesignText } from "./designIntent";
import { cleanText, compactValue, listFromValue, nowIso } from "./briefText";

export function baseBriefFromForm(
  formState: Partial<CreateFormData>,
  options: {
    contextText?: string;
    hasFrontImage: boolean;
    hasBackImage: boolean;
  }
): DesignBrief {
  const derived = deriveDesignBrief(formState, options);
  const provenance: NonNullable<DesignBrief["provenance"]> = {};
  const markRestoredScalar = (field: string, value: unknown) => {
    if (compactValue(value)) {
      provenance[field] = { source: "legacy", confirmed: true };
    }
  };
  markRestoredScalar("visual.prompt", derived.front.prompt);
  markRestoredScalar("occasion", derived.occasion);
  markRestoredScalar("visual.primaryColor", derived.front.color);
  markRestoredScalar("visual.length", derived.front.length);
  markRestoredScalar("visual.sleeve", derived.front.sleeve);
  markRestoredScalar("visual.neckline", derived.front.neckline);
  markRestoredScalar("visual.waistline", derived.front.waistline);
  markRestoredScalar("visual.silhouette", derived.front.silhouette);
  markRestoredScalar("visual.fabric", derived.front.fabric);
  markRestoredScalar("visual.backPrompt", derived.back.prompt);
  markRestoredScalar("constraints.modesty", derived.constraints.modesty);
  return {
    version: 2,
    userGoal:
      derived.front.prompt ??
      cleanText(committedDesignText(options.contextText)),
    occasion: derived.occasion,
    constraints: {
      mustHave: derived.constraints.mustHave,
      avoid: derived.constraints.avoid,
      comfort: derived.constraints.comfort,
      budget: derived.constraints.budget,
      mobility: derived.constraints.mobility,
      bodyConcerns: [],
      modesty: derived.constraints.modesty,
      coverage:
        derived.constraints.modesty === "modest"
          ? ["modest full-coverage design"]
          : derived.constraints.modesty === "balanced"
            ? ["balanced coverage"]
            : derived.constraints.modesty === "revealing"
              ? ["revealing/open coverage is permitted"]
              : [],
      support: [],
      opacity: [],
      accessibility: [],
      cultural: [],
      construction: [],
      referenceGuidance: [],
      color: {
        primary: compactValue(derived.front.color),
        accents: [],
        preferred: listFromValue(formState.color),
        rejected: derived.constraints.avoid.filter(item =>
          /\b(?:black|white|ivory|champagne|gold|red|blue|green|pink|blush)\b/i.test(
            item
          )
        ),
      },
    },
    rejectedIdeas: derived.constraints.avoid,
    openQuestions: [],
    front: {
      prompt: derived.front.prompt,
      color: compactValue(derived.front.color),
      length: derived.front.length,
      sleeve: derived.front.sleeve,
      sleeveAddons: derived.front.sleeveAddons,
      neckline: derived.front.neckline,
      waistline: derived.front.waistline,
      silhouette: derived.front.silhouette,
      fabric: compactValue(derived.front.fabric),
      mood: [],
      details: [],
    },
    back: {
      prompt: derived.back.prompt,
      details: derived.back.prompt ? [derived.back.prompt] : [],
    },
    references: derived.references,
    ...(Object.keys(provenance).length > 0 ? { provenance } : {}),
    pendingAction: { type: "none" },
    updatedAt: nowIso(),
  };
}

/**
 * Compile the current editable contract, including restored/gallery sessions
 * that predate conversational DesignBrief persistence. A populated legacy
 * form is still renderable state; deriving a v2 brief prevents those actions
 * from silently producing an unversioned image (`designSpec: null`).
 */
export function compileCurrentDesignSpec(
  designBrief: DesignBrief | null | undefined,
  formState: Partial<CreateFormData>,
  options: {
    hasFrontImage?: boolean;
    hasBackImage?: boolean;
  } = {}
): DesignSpec | undefined {
  const effectiveBrief =
    designBrief ??
    baseBriefFromForm(formState, {
      hasFrontImage: options.hasFrontImage ?? false,
      hasBackImage: options.hasBackImage ?? false,
    });
  return compileDesignSpec(effectiveBrief, formState);
}
